
import React from "react";
import { format } from "date-fns";
import { Pencil, XCircle, CalendarX, Video, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StudentEvent } from "../types";
import { cn } from "@/lib/utils";
import { getEventColor, getEventStatus, showCancelledOverlay } from "../utils/eventUtils";
import { useTranslations } from "@/hooks/useTranslations";

interface CalendarEventProps {
  event: StudentEvent;
  weekDays: Date[];
  calculateEventPosition: (time: Date) => number;
  handleEventClick: (event: StudentEvent) => void;
}

export const CalendarEvent: React.FC<CalendarEventProps> = ({
  event,
  weekDays,
  calculateEventPosition,
  handleEventClick
}) => {
  const { t } = useTranslations("schedule");
  const hasOverlay = showCancelledOverlay(event);
  const displayStatus = getEventStatus(event, t);

  const getEventIcon = (event: StudentEvent) => {
    if (event.status === 'pending_cancellation') {
      return <CalendarX className="h-5 w-5 text-red-500 dark:text-red-400 absolute right-2 top-2" />;
    }
    return <XCircle className="h-5 w-5 text-red-500 dark:text-red-400 absolute right-2 top-2" />;
  };

  return (
    <div
      key={event.id}
      className={cn(
        "absolute p-2 rounded-lg transition-all duration-200",
        getEventColor(event),
        hasOverlay && "relative"
      )}
      style={{
        top: `${calculateEventPosition(new Date(event.start_time))}px`,
        left: `calc(60px + (${weekDays.findIndex(day => 
          day.getDate() === new Date(event.start_time).getDate() && 
          day.getMonth() === new Date(event.start_time).getMonth() && 
          day.getFullYear() === new Date(event.start_time).getFullYear()
        )} * ((100% - 60px) / ${weekDays.length})))`,
        width: `calc(((100% - 60px) / ${weekDays.length}) - 10px)`,
        height: `${((new Date(event.end_time).getTime() - new Date(event.start_time).getTime()) / (1000 * 60)) * (60 / 60)}px`,
        minHeight: "20px",
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (event.isCurrentStudent) {
          handleEventClick(event);
        }
      }}
    >
      <div className="flex flex-col h-full">
        <div className="flex-1">
          <div className="flex items-center justify-between gap-1 text-sm">
            <span className="font-medium truncate flex items-center gap-1">
              {event.is_online && <Globe className="h-3 w-3" />}
              {event.is_video && <Video className="h-3 w-3" />}
              {displayStatus}
            </span>
            <span className="whitespace-nowrap">
              {format(new Date(event.start_time), "HH:mm")}
            </span>
          </div>
        </div>
        
        {event.isCurrentStudent && !hasOverlay && (
          <div className="flex justify-end gap-1 mt-2">
            <Button 
              variant="ghost" 
              size="sm"
              className="h-6 w-6 p-0 hover:bg-white/50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700/50"
              onClick={(e) => {
                e.stopPropagation();
                handleEventClick(event);
              }}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          </div>
        )}
        
        {hasOverlay && (
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{
              background: "url(\"data:image/svg+xml,%3Csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='0' y1='100%25' x2='100%25' y2='0' stroke='%23EF4444' stroke-width='2'/%3E%3C/svg%3E\")"
            }}
          >
            {getEventIcon(event)}
          </div>
        )}
      </div>
    </div>
  );
};
